/**
 * The design checks panel (requirements 13.1, 9.3, 12.9): the left side of
 * the Design checks step, where the palette is during schematic capture.
 *
 * Checking is its own step of the workflow, apart from drawing: during capture
 * a half-drawn design fails most rules, and findings nobody asked for are
 * noise. Here the designer chooses the profile, runs the check, and works
 * through the findings: each one names its severity, the element it is about
 * and the message in full, and clicking it selects that element on the canvas
 * beside the list, with the inspector showing the field it names. The canvas
 * stays editable, so a finding can be fixed where it is found.
 *
 * Checking runs on demand (design §11), so findings can lag the drawing. The
 * panel says when they were produced and whether the design has changed since.
 */
import { useEffect, useRef, type ReactNode } from "react";
import type { CheckReport } from "../api/client";
import type { Finding } from "../design/document";
import { findingElement } from "./parameters";

export interface FindingsPanelProps {
  /** The last check, or null when none has run. */
  report: CheckReport | null;
  /** Why checking could not run (no profile, a failed request), if it could not. */
  checkProblem: string | null;
  checking: boolean;
  /** True when the working document has changed since `report` was produced. */
  stale: boolean;
  profileName: string | null;
  /** The control that chooses the profile to check against. */
  profile?: ReactNode;
  /** Findings from a save the server refused. Shown first: nothing is saved until they are fixed. */
  saveFindings: readonly Finding[];
  /** Element refs present in the design, so a finding about one can be navigated to. */
  present: ReadonlySet<string>;
  /** An element whose findings to bring forward (from a badge or menu). */
  focus: string | null;
  onNavigate: (ref: string) => void;
  onCheck: () => void;
  canCheck: boolean;
}

const SEVERITIES = ["error", "warning", "info"] as const;

/** The mark beside a finding. Shape as well as colour, as the canvas badges do. */
const MARK: Record<string, string> = { error: "!", warning: "!", info: "i" };

function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? "" : "s"}`;
}

function relativeTime(iso: string): string {
  const seconds = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 45) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${plural(minutes, "min")} ago`;
  return new Date(iso).toLocaleString();
}

function severityOf(finding: Finding): (typeof SEVERITIES)[number] {
  return SEVERITIES.includes(finding.severity as (typeof SEVERITIES)[number])
    ? (finding.severity as (typeof SEVERITIES)[number])
    : "info";
}

/** One finding as a row: severity, element, the message in full. */
function FindingRow({
  finding,
  present,
  focused,
  onNavigate,
}: {
  finding: Finding;
  present: ReadonlySet<string>;
  focused: boolean;
  onNavigate: (ref: string) => void;
}) {
  const element = findingElement(finding);
  const navigable = element !== null && present.has(element);
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (focused) ref.current?.scrollIntoView?.({ block: "nearest" });
  }, [focused]);
  const severity = severityOf(finding);
  return (
    <li>
      <button
        ref={ref}
        type="button"
        className={`ws-finding ws-sev-${severity}${focused ? " ws-finding-focused" : ""}`}
        aria-label={`${severity} at ${element ?? "design"}: ${finding.message}`}
        aria-disabled={navigable ? undefined : true}
        title={navigable ? "Show on the canvas" : undefined}
        onClick={() => navigable && onNavigate(element)}
      >
        <span className="ws-flag-mark" aria-hidden="true">
          {MARK[severity]}
        </span>
        <span className="ws-finding-body" aria-hidden="true">
          <span className="ws-finding-ref">{element ?? "design"}</span>
          <span className="ws-finding-message">{finding.message}</span>
          <span className="ws-finding-meta">
            {finding.code.replace(/_/g, " ")}
            {finding.path && finding.path !== element ? ` · ${finding.path}` : ""}
          </span>
        </span>
      </button>
    </li>
  );
}

function FindingList({
  findings,
  present,
  focus,
  onNavigate,
  label,
  prefix,
}: {
  findings: readonly Finding[];
  present: ReadonlySet<string>;
  focus: string | null;
  onNavigate: (ref: string) => void;
  label: string;
  prefix: string;
}) {
  // Errors first, then warnings, then notes; the server's order within each.
  const ordered = [...findings].sort((a, b) => SEVERITIES.indexOf(severityOf(a)) - SEVERITIES.indexOf(severityOf(b)));
  return (
    <ul className="ws-findings" aria-label={label}>
      {ordered.map((finding, index) => (
        <FindingRow
          key={`${prefix}-${finding.code}-${finding.path}-${index}`}
          finding={finding}
          present={present}
          focused={focus !== null && findingElement(finding) === focus}
          onNavigate={onNavigate}
        />
      ))}
    </ul>
  );
}

export function FindingsPanel(props: FindingsPanelProps) {
  const { report, saveFindings, present, focus, onNavigate } = props;
  const findings = report?.findings ?? [];
  const counts = SEVERITIES.map((severity) => [severity, findings.filter((f) => f.severity === severity).length] as const);

  let status: string;
  if (props.checking) status = "Checking…";
  else if (props.checkProblem) status = props.checkProblem;
  else if (!report) status = "Not checked yet";
  else
    status = `${props.profileName ?? "the profile"} · ${relativeTime(report.checked_at)}${props.stale ? " · changed since" : ""}`;
  const deferred = report?.deferred ?? [];

  return (
    <aside className="ws-checks" aria-label="Design checks">
      <div className="ws-checks-head">
        {props.profile}
        <button type="button" className="primary" onClick={props.onCheck} disabled={!props.canCheck || props.checking}>
          {report ? "Check again" : "Check"}
        </button>
      </div>
      <p className="ws-muted ws-small ws-checks-status" role="status">
        <span
          title={
            report
              ? `Checked against ${props.profileName ?? "the profile"} at ${new Date(report.checked_at).toLocaleString()}`
              : undefined
          }
        >
          {status}
        </span>
      </p>

      {saveFindings.length > 0 && (
        <section className="ws-save-refused" role="alert" aria-label="Save refused">
          <h3>Save refused</h3>
          <p className="ws-small">Nothing since has reached the server until these are fixed.</p>
          <FindingList
            findings={saveFindings}
            present={present}
            focus={null}
            onNavigate={onNavigate}
            label="Why the save was refused"
            prefix="save"
          />
        </section>
      )}

      {report && (
        <section aria-label="Findings">
          <h3 className="ws-checks-title">
            Findings
            <span className="ws-counts">
              {counts.map(([severity, count]) =>
                count > 0 ? (
                  <span key={severity} className={`ws-count ws-sev-${severity}`} title={plural(count, severity)}>
                    <span className="ws-flag-mark" aria-hidden="true">
                      {MARK[severity]}
                    </span>
                    <span className="visually-hidden">{plural(count, severity)}</span>
                    <span aria-hidden="true">{count}</span>
                  </span>
                ) : null,
              )}
            </span>
          </h3>
          {findings.length === 0 ? (
            <p className="ws-muted ws-small">No findings against this profile.</p>
          ) : (
            <FindingList
              findings={findings}
              present={present}
              focus={focus}
              onNavigate={onNavigate}
              label="Check findings"
              prefix="check"
            />
          )}
        </section>
      )}

      {deferred.length > 0 && (
        <details className="ws-deferred">
          <summary className="ws-small">{plural(deferred.length, "check")} deferred</summary>
          <ul className="ws-small">
            {deferred.map((check, index) => (
              <li key={index}>
                {check.description} <span className="ws-muted">({check.reason})</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </aside>
  );
}
